import type { keepPreviousData } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';

import { getPublicConfig } from '@sola/config/lib/getters.ts';

import { graphql } from '../gql/gql.ts';
import { normalizeItems } from './normalize.ts';
import type {
  ArticleSearchInput,
  AutocompletePostalCodeResponseDto,
  CatalogSearchInput,
  IndexInput,
  LiveSlotDataDto,
  PostalCodeResultDto,
  PrivateEntryInput,
  PrivateSearchInput,
  PublishedSearchInput,
  TagCountsInput,
  VolunteerLocationItemDto,
  VolunteerLocationsResponseDto,
  VolunteerSearchInput,
} from './schema.ts';
import {
  PublishedEntryResponseDto,
  QueryResponsePrivate,
  QueryResponsePublished,
  TagCountsResult,
} from './schema.ts';

// ─── Config ──────────────────────────────────────────────────────────────────

// @TODO: Move all this logic to a provider, etc.
const getGraphQLClient = () => {
  // TODO secrets

  const {
    NEXT_PUBLIC_SKIPPY_SEARCH_API_URL,
    NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY,
  } = getPublicConfig();

  if (
    !NEXT_PUBLIC_SKIPPY_SEARCH_API_URL ||
    !NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY
  ) {
    throw new Error(
      'Missing environment variable for SKIPPY_SEARCH_API_URL or API_KEY. Please confirm your environment (Vite or Next) set it in your .env file.',
    );
  }

  return new GraphQLClient(NEXT_PUBLIC_SKIPPY_SEARCH_API_URL, {
    headers: {
      'x-api-key': NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY,
    } as Record<string, string>,
  });
};

const queryKeys = {
  listSearchResults: 'listSearchResults',
  listArticles: 'listArticles',
  listCatalog: 'listCatalog',
  listVolunteerLocations: 'listVolunteerLocations',
  listPrivateSearchResults: 'listPrivateSearchResults',
  autocompletePostalCode: 'autocompletePostalCode',
  volunteerLiveSlots: 'volunteerLiveSlots',
  getTagCounts: 'getTagCounts',
};

// ─── Fragments ───────────────────────────────────────────────────────────────

export const publishedFieldsFragment = graphql(`
  fragment PublishedFields on PublishedEntry {
    contentfulEntryID
    contentfulType
    pageTitle
    summary
    searchKeywords
    path
    media {
      image {
        url
        alt
      }
      video {
        url
      }
      caption
    }
    contributors {
      name
      position
      image {
        url
      }
    }
    template
    fund {
      id
      tcmId
      fundTitle
      fundCodeOneTime
      fundCodeCommitment
      catalogAmount
    }
    taxonomy {
      articleType
      articleTopics
      articleThemes
      fundPromotion
      fundCategory
      programCountry
      programRegion
      other
    }
    datePublished
    dateModified
    dateIndexed
  }
`);

export const privateFieldsPageFragment = graphql(`
  fragment PrivateFieldsPage on PrivateEntry {
    pageTitle
    summary
    searchKeywords
    path
  }
`);

export const privateFieldsContentFragment = graphql(`
  fragment PrivateFieldsContent on PrivateEntry {
    title
  }
`);

export const privateFieldsFragment = graphql(`
  fragment PrivateFields on PrivateEntry {
    contentfulEntryID
    contentfulType
    adminTitle
    ...PrivateFieldsPage
    ...PrivateFieldsContent
    isReferenced
    parentPageId
    isOrphaned
    hasChildren
    status
    dateModified
    dateIndexed
  }
`);

export const volunteerLocationFieldsFragment = graphql(`
  fragment VolunteerLocationFields on VolunteerLocationEntry {
    eventId
    slug
    locationName
    tourId
    tourName
    description
    address1
    address2
    city
    state
    postalCode
    country
    startDate
    endDate
    point {
      lat
      lon
    }
    openSlots
    totalSlots
    status
    dateIndexed
    dressCode
    dressCodeDetails
    specialEventDetails
    partnerWebsite
    lastModifiedDate
    volunteerCoordinatorName
    volunteerCoordinatorEmail
    eventContactName
    eventContactEmail
    eventContactPhone
    venueName
    roles
    image {
      url
    }
  }
`);

// ─── Published Queries & Hooks ───────────────────────────────────────────────

const listSearchResultsQuery = graphql(`
  query listSearchResults($input: PublishedSearchInput!) {
    listSearchResults(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const listArticlesQuery = graphql(`
  query listArticles($input: ArticleSearchInput!) {
    listArticles(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const listCatalogQuery = graphql(`
  query listCatalog($input: CatalogSearchInput!) {
    listCatalog(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const getTagCountsQueryDoc = graphql(`
  query getTagCounts($input: TagCountsInput) {
    getTagCounts(input: $input) {
      topics {
        tag
        count
      }
      themes {
        tag
        count
      }
      countries {
        tag
        count
      }
    }
  }
`);

const normalizeSearchResults = (data: QueryResponsePublished) => {
  const normalized = normalizeItems(data.items);
  const items = normalized.reduce<QueryResponsePublished['items']>(
    (acc, item) => {
      const result = PublishedEntryResponseDto.safeParse(item);
      if (result.success) {
        acc.push(result.data);
      } else {
        console.warn(
          `[search] Excluded item from results (failed validation):`,
          item.contentfulEntryID,
          result.error.issues,
        );
      }
      return acc;
    },
    [],
  );
  return { ...data, items };
};

export const getListSearchResultsQuery = async (
  input: PublishedSearchInput,
) => {
  const graphQLClient = getGraphQLClient();
  const response = await graphQLClient.request<{
    listSearchResults: QueryResponsePublished;
  }>(listSearchResultsQuery, {
    input,
  });
  return normalizeSearchResults(response.listSearchResults);
};

/**
 * Exhaustive tag counts per taxonomy (topics/themes/countries) for a given
 * article type. Backed by an aggregation with size 100, so unlike the
 * top-20 search facets this does not truncate long-tail tags — used by the
 * sitemap to enumerate tag pages.
 */
export const getTagCountsQuery = async (
  input: TagCountsInput = {},
): Promise<TagCountsResult> => {
  const graphQLClient = getGraphQLClient();
  const response = await graphQLClient.request<{
    getTagCounts: TagCountsResult;
  }>(getTagCountsQueryDoc, {
    input,
  });
  return TagCountsResult.parse(response.getTagCounts);
};

export const useListSearchResultsQuery = (
  input: PublishedSearchInput,
  options?: { enabled?: boolean; placeholderData?: typeof keepPreviousData },
) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listSearchResults, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listSearchResults: QueryResponsePublished;
      }>(listSearchResultsQuery, {
        input,
      });
      return normalizeSearchResults(response.listSearchResults);
    },
    ...options,
  });
};

export const useListArticlesQuery = (
  input: ArticleSearchInput,
  options?: { placeholderData?: typeof keepPreviousData },
) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listArticles, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listArticles: QueryResponsePublished;
      }>(listArticlesQuery, {
        input,
      });
      return QueryResponsePublished.parse(response.listArticles);
    },
    ...options,
  });
};

export const useListCatalogQuery = (input: CatalogSearchInput) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listCatalog, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listCatalog: QueryResponsePublished;
      }>(listCatalogQuery, {
        input,
      });
      return QueryResponsePublished.parse(response.listCatalog);
    },
  });
};

// ─── Private Queries & Hooks ─────────────────────────────────────────────────

const listPrivateSearchResultsQuery = graphql(`
  query listPrivateSearchResults($input: PrivateSearchInput!) {
    listPrivateSearchResults(input: $input) {
      totalCount
      items {
        ...PrivateFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

export const useListPrivateSearchResultsQuery = (
  input: PrivateSearchInput,
  options?: { placeholderData?: typeof keepPreviousData; enabled?: boolean },
) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listPrivateSearchResults, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listPrivateSearchResults: QueryResponsePrivate;
      }>(listPrivateSearchResultsQuery, {
        input,
      });
      return QueryResponsePrivate.parse(response.listPrivateSearchResults);
    },
    ...options,
  });
};

export const useFetchPrivateChildren = () => {
  const queryClient = useQueryClient();
  const graphQLClient = getGraphQLClient();

  const fetchChildren = async (parentId: string) => {
    const data = await queryClient.fetchQuery({
      queryKey: [queryKeys.listPrivateSearchResults, parentId],
      queryFn: async () => {
        const res = await graphQLClient.request(listPrivateSearchResultsQuery, {
          input: {
            parentPageId: parentId,
            perPage: 1000,
            sort: 'path',
            sortDir: 'asc',
          } satisfies PrivateSearchInput,
        });
        return res?.listPrivateSearchResults?.items as PrivateEntryInput[];
      },
    });

    return data;
  };

  return { fetchChildren };
};

// ─── Volunteer Queries & Hooks ───────────────────────────────────────────────

const listVolunteerLocationsQuery = graphql(`
  query listVolunteerLocations($input: VolunteerSearchInput) {
    listVolunteerLocations(input: $input) {
      totalCount
      items {
        ...VolunteerLocationFields
        tourCount
        tourEvents {
          eventId
          city
          state
          startDate
          endDate
          openSlots
          totalSlots
        }
      }
    }
  }
`);

const getVolunteerLocationByIdQuery = graphql(`
  query getVolunteerLocationById($input: VolunteerLocationByIdInput!) {
    getVolunteerLocationById(input: $input) {
      ...VolunteerLocationFields
      tourCount
    }
  }
`);

const getVolunteerLocationBySlugQuery = graphql(`
  query getVolunteerLocationBySlug($input: VolunteerLocationBySlugInput!) {
    getVolunteerLocationBySlug(input: $input) {
      ...VolunteerLocationFields
    }
  }
`);

const getVolunteerLiveSlotsQuery = graphql(`
  query getVolunteerLiveSlots($input: VolunteerLiveSlotsInput!) {
    getVolunteerLiveSlots(input: $input) {
      totalOpenSlots
      totalSlots
      roleCounts {
        role
        openSlots
        totalSlots
      }
      slots {
        id
        eventLocationId
        startDate
        endDate
        status
        description
      }
    }
  }
`);

export const useListVolunteerLocationsQuery = (
  input: VolunteerSearchInput,
  options?: { placeholderData?: typeof keepPreviousData },
) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listVolunteerLocations, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listVolunteerLocations: VolunteerLocationsResponseDto;
      }>(listVolunteerLocationsQuery, {
        input,
      });
      return response.listVolunteerLocations;
    },
    ...options,
  });
};

/** Extract partial data from a graphql-request ClientError response. */
const extractPartialData = <T>(error: unknown, key: string, fallback: T): T => {
  const clientError = error as {
    response?: { data?: Record<string, unknown> };
  };
  const data = clientError?.response?.data;
  return (data?.[key] as T) ?? fallback;
};

/** Generic server-side fetch with partial data extraction on error. */
const fetchWithPartial = async <T>(
  document: unknown,
  variables: Record<string, unknown>,
  key: string,
  fallback: T,
): Promise<T> => {
  const graphQLClient = getGraphQLClient();
  try {
    const response = await (
      graphQLClient.request as (doc: unknown, vars: unknown) => Promise<unknown>
    )(document, variables);
    return (response as Record<string, T>)[key];
  } catch (error) {
    return extractPartialData<T>(error, key, fallback);
  }
};

/** Server-side: fetch a volunteer location by eventId or slug. */
export const fetchVolunteerLocation = async (
  filter: { eventId: string } | { slug: string },
): Promise<VolunteerLocationItemDto | null> => {
  if ('eventId' in filter) {
    return fetchWithPartial<VolunteerLocationItemDto | null>(
      getVolunteerLocationByIdQuery,
      { input: { eventId: filter.eventId } },
      'getVolunteerLocationById',
      null,
    );
  }
  return fetchWithPartial<VolunteerLocationItemDto | null>(
    getVolunteerLocationBySlugQuery,
    { input: { slug: filter.slug } },
    'getVolunteerLocationBySlug',
    null,
  );
};

/** Server-side: list all locations for a tour by slug. */
export const fetchVolunteerLocationsBySlug = async (
  slug: string,
): Promise<VolunteerLocationsResponseDto> => {
  return fetchWithPartial<VolunteerLocationsResponseDto>(
    listVolunteerLocationsQuery,
    { input: { slug, perPage: 100 } },
    'listVolunteerLocations',
    { items: [], totalCount: 0 },
  );
};

/** Client-side hook to fetch live volunteer slot counts. Runs on mount, refreshes every minute. */
export const useLiveVolunteerSlotsQuery = (eventId: string | undefined) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.volunteerLiveSlots, eventId],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        getVolunteerLiveSlots: LiveSlotDataDto;
      }>(getVolunteerLiveSlotsQuery, { input: { eventId } });
      return response.getVolunteerLiveSlots;
    },
    enabled: !!eventId,
    staleTime: 60_000,
  });
};

// ─── Geo / Autocomplete ──────────────────────────────────────────────────────

const autocompletePostalCodeQuery = graphql(`
  query autocompletePostalCode($input: AutocompletePostalCodeInput!) {
    autocompletePostalCode(input: $input) {
      items {
        postalCode
        city
        state
        lat
        lng
      }
    }
  }
`);

export const useAutocompletePostalCodeQuery = (
  query: string,
  options?: { limit?: number; enabled?: boolean },
) => {
  const graphQLClient = getGraphQLClient();
  const input = { query, limit: options?.limit ?? 10 };
  return useQuery({
    queryKey: [queryKeys.autocompletePostalCode, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        autocompletePostalCode: AutocompletePostalCodeResponseDto;
      }>(autocompletePostalCodeQuery, { input });
      return response.autocompletePostalCode.items;
    },
    enabled: (options?.enabled ?? true) && query.length >= 2,
  });
};

/** Imperative fetch for postal code autocomplete. Use when you need results outside of React Query's lifecycle. */
export const fetchAutocompletePostalCode = async (
  query: string,
  limit = 10,
): Promise<PostalCodeResultDto[]> => {
  const graphQLClient = getGraphQLClient();
  const response = await graphQLClient.request<{
    autocompletePostalCode: AutocompletePostalCodeResponseDto;
  }>(autocompletePostalCodeQuery, { input: { query, limit } });
  return response.autocompletePostalCode.items;
};

// ─── Mutations ───────────────────────────────────────────────────────────────

const indexEntryMutation = graphql(`
  mutation indexEntry($input: IndexInput!) {
    indexEntry(input: $input) {
      success
      error
    }
  }
`);

const indexPrivateEntryMutation = graphql(`
  mutation indexPrivateEntry($input: IndexInput!) {
    indexPrivateEntry(input: $input) {
      success
      error
    }
  }
`);

const removeIndexPrivateEntryMutation = graphql(`
  mutation removeIndexPrivateEntry($input: IndexInput!) {
    removePrivateEntry(input: $input) {
      success
      error
    }
  }
`);

export const useIndexEntryMutation = () => {
  const queryClient = useQueryClient();
  const graphQLClient = getGraphQLClient();
  return useMutation({
    mutationFn: async (input: IndexInput) =>
      graphQLClient.request(indexEntryMutation, { input }),
    onSuccess: () => {
      // @TODO: Does this work with pagination?
      queryClient.invalidateQueries({
        queryKey: [queryKeys.listSearchResults],
      });
    },
  });
};

export const useIndexPrivateEntryMutation = () => {
  const queryClient = useQueryClient();
  const graphQLClient = getGraphQLClient();
  return useMutation({
    mutationFn: async (input: IndexInput) =>
      graphQLClient.request(indexPrivateEntryMutation, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.listPrivateSearchResults],
      });
    },
  });
};

export const useDeleteIndexPrivateEntryMutation = () => {
  const queryClient = useQueryClient();
  const graphQLClient = getGraphQLClient();
  return useMutation({
    mutationFn: async (input: IndexInput) =>
      graphQLClient.request(removeIndexPrivateEntryMutation, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.listPrivateSearchResults],
        exact: false,
      });
    },
  });
};
